(function (factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  api.boot();
})(function () {
  'use strict';

  const MAX_SWIMMERS = 5;
  const CART_TRIGGER_SELECTOR = '[data-testid="cart-drawer-trigger"]';
  const productCache = new Map();
  const revisions = new WeakMap();
  let scheduledFrame = 0;

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim().toLowerCase()).filter(Boolean);
    return [];
  }

  function classifyLivestockKind(product) {
    if (!product || typeof product !== 'object') return '';
    const tags = normalizeTags(product.tags);
    const tagSet = new Set(tags);
    const type = String(product.type || product.product_type || '').trim().toLowerCase();
    const title = String(product.title || '').trim().toLowerCase();
    if (tagSet.has('dropship') || tagSet.has('shopify collective')) return '';
    if (tagSet.has('aquarium-supply') || tagSet.has('shrimp-food') || tagSet.has('shrimp-supply')) return '';
    const shrimp = type.includes('shrimp') || title.includes('shrimp') || tagSet.has('live-shrimp') || tagSet.has('freshwater-shrimp') || tagSet.has('neocaridina') || tagSet.has('caridina');
    if (shrimp) return 'shrimp';
    const fish = type.includes('live animals') || type.includes('live medaka') || type.includes('fish') || tagSet.has('live fish') || tagSet.has('live-fish') || tagSet.has('medaka') || tagSet.has('aquaphoria-bred') || tagSet.has('aquaphoria bred medaka');
    if (fish) return 'fish';
    if (tagSet.has('livestock')) return 'fish';
    return '';
  }

  function classifyProduct(product) { return Boolean(classifyLivestockKind(product)); }

  function productJsonUrl(href, origin) {
    if (!href) return '';
    try {
      const url = new URL(href, origin || (typeof location !== 'undefined' ? location.origin : undefined));
      const parts = url.pathname.split('/').filter(Boolean);
      if (!parts.includes('products')) return '';
      url.search = ''; url.hash = '';
      url.pathname = `${url.pathname.replace(/\/+$/, '')}.js`;
      return url.toString();
    } catch (_) { return ''; }
  }

  function hashSeed(value) {
    let hash = 2166136261;
    const text = String(value || '');
    for (let i = 0; i < text.length; i += 1) { hash ^= text.charCodeAt(i); hash = Math.imul(hash, 16777619); }
    return hash >>> 0;
  }
  function numberFromSeed(seed, shift, min, max) { return min + (max - min) * (((seed >>> shift) & 0xff) / 255); }

  function readRows(target) {
    return [...target.querySelectorAll('.cart-items__table-row[data-key]')].map((row) => {
      const link = row.querySelector('.cart-items__title[href]');
      const input = row.querySelector('input[name="updates[]"], input[name^="updates"], input[type="number"]');
      const quantity = Number.parseInt(input?.value || '1', 10);
      return {
        key: row.dataset.key || '', title: link?.textContent?.trim() || '',
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        jsonUrl: productJsonUrl(link?.href || '', typeof location !== 'undefined' ? location.origin : '')
      };
    }).filter((item) => item.key && item.title && item.jsonUrl);
  }

  async function getProductKind(item) {
    if (productCache.has(item.jsonUrl)) return productCache.get(item.jsonUrl);
    try {
      const response = await fetch(item.jsonUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return '';
      const kind = classifyLivestockKind(await response.json());
      productCache.set(item.jsonUrl, kind);
      return kind;
    } catch (_) { return ''; }
  }

  function createPond() {
    const pond = document.createElement('section');
    pond.className = 'aq-mini-pond';
    pond.dataset.aqMiniPond = ''; pond.dataset.aqMiniPondCount = '0';
    pond.setAttribute('aria-label', 'Mini Pond cart preview');
    pond.innerHTML = `
      <div class="aq-mini-pond__head"><div><p class="aq-mini-pond__kicker">Mini Pond</p><h2>Good things swim together.</h2></div><p class="aq-mini-pond__status" data-aq-mini-pond-status aria-live="polite">Your pond is quiet.</p></div>
      <div class="aq-mini-pond__water" data-aq-mini-pond-water aria-hidden="true"><span class="aq-mini-pond__empty">Your pond is ready for its first livestock selection.</span></div>
      <p class="aq-mini-pond__foot">Representative fish or shrimp per livestock selection · supplies stay in the list below.</p>`;
    return pond;
  }
  function findDirectPond(target) { return [...target.children].find((node) => node.hasAttribute?.('data-aq-mini-pond')) || null; }
  function ensurePond(target) { let pond = findDirectPond(target); if (!pond) { pond = createPond(); target.prepend(pond); } return pond; }

  function setCartBadge(count) {
    const trigger = document.querySelector(CART_TRIGGER_SELECTOR); if (!trigger) return;
    trigger.classList.add('aq-v4-cart-pond-badge');
    trigger.classList.toggle('aq-v4-cart-pond-badge--live', count > 0);
    trigger.dataset.aqPondLivestock = String(Math.max(0, count));
  }

  function applySeededMotion(node, key, kind) {
    const seed = hashSeed(key);
    node.style.setProperty('--aq-pond-x', `${numberFromSeed(seed, 0, 9, 70).toFixed(1)}%`);
    node.style.setProperty('--aq-pond-y', `${numberFromSeed(seed, 8, kind === 'shrimp' ? 42 : 15, kind === 'shrimp' ? 76 : 70).toFixed(1)}%`);
    node.style.setProperty('--aq-pond-dx', `${numberFromSeed(seed, 16, kind === 'shrimp' ? -18 : -34, kind === 'shrimp' ? 18 : 34).toFixed(1)}px`);
    node.style.setProperty('--aq-pond-dy', `${numberFromSeed(seed, 24, kind === 'shrimp' ? -9 : -22, kind === 'shrimp' ? 9 : 22).toFixed(1)}px`);
    node.style.setProperty('--aq-pond-duration', `${numberFromSeed(seed ^ 0x9e3779b9, 4, kind === 'shrimp' ? 12 : 8.5, kind === 'shrimp' ? 18 : 14.5).toFixed(2)}s`);
    node.style.setProperty('--aq-pond-delay', `${(-numberFromSeed(seed ^ 0x85ebca6b, 12, 0, 5.5)).toFixed(2)}s`);
    node.style.setProperty('--aq-pond-scale', numberFromSeed(seed ^ 0xc2b2ae35, 20, kind === 'shrimp' ? .72 : .84, kind === 'shrimp' ? .96 : 1.14).toFixed(2));
    node.classList.toggle('aq-mini-pond__swimmer--reverse', Boolean(seed & 1));
  }

  function fishMarkup() {
    return `<span class="aq-mini-pond__fish"><span class="aq-mini-pond__tail"></span><span class="aq-mini-pond__pectoral aq-mini-pond__pectoral--top"></span><span class="aq-mini-pond__pectoral aq-mini-pond__pectoral--bottom"></span><span class="aq-mini-pond__dorsal"></span><span class="aq-mini-pond__body"></span><span class="aq-mini-pond__shine"></span></span><span class="aq-mini-pond__qty"></span>`;
  }
  function shrimpMarkup() {
    return `<span class="aq-mini-pond__fish" style="inset:5px 3px"><span class="aq-mini-pond__tail" style="left:5px;top:9px;width:15px;height:13px;background:rgba(231,154,116,.66);clip-path:polygon(100% 50%,0 4%,28% 50%,0 96%)"></span><span class="aq-mini-pond__body" style="left:17px;top:9px;width:34px;height:11px;border-radius:70% 45% 58% 70%;background:linear-gradient(180deg,rgba(246,189,154,.88),rgba(198,103,83,.82));box-shadow:inset -7px 0 8px rgba(74,30,30,.14),0 0 13px rgba(235,153,118,.18)"></span><span style="position:absolute;left:44px;top:7px;width:18px;height:1px;background:rgba(244,207,184,.72);transform:rotate(-18deg);transform-origin:left center"></span><span style="position:absolute;left:44px;top:18px;width:18px;height:1px;background:rgba(244,207,184,.6);transform:rotate(18deg);transform-origin:left center"></span><span class="aq-mini-pond__shine" style="left:26px;top:12px;width:14px"></span></span><span class="aq-mini-pond__qty"></span>`;
  }

  function upsertSwimmer(water, item) {
    let node = [...water.querySelectorAll('[data-aq-mini-pond-swimmer]')].find((candidate) => candidate.dataset.aqMiniPondSwimmer === item.key);
    const kind = item.kind === 'shrimp' ? 'shrimp' : 'fish';
    if (!node) { node = document.createElement('span'); node.className = 'aq-mini-pond__swimmer'; node.dataset.aqMiniPondSwimmer = item.key; water.append(node); }
    if (node.dataset.aqMiniPondKind !== kind) { node.dataset.aqMiniPondKind = kind; node.innerHTML = kind === 'shrimp' ? shrimpMarkup() : fishMarkup(); }
    node.title = `${item.title} · representative Mini Pond ${kind}`;
    const quantity = node.querySelector('.aq-mini-pond__qty'); if (quantity) quantity.textContent = item.quantity > 1 ? `×${item.quantity}` : '';
    applySeededMotion(node, item.key, kind);
  }

  async function reconcile(pond, target) {
    const water = pond.querySelector('[data-aq-mini-pond-water]'); const status = pond.querySelector('[data-aq-mini-pond-status]');
    if (!water || !status) return 0;
    const revision = (revisions.get(pond) || 0) + 1; revisions.set(pond, revision);
    const classified = await Promise.all(readRows(target).map(async (item) => ({ ...item, kind: await getProductKind(item) })));
    if (revisions.get(pond) !== revision || !pond.isConnected) return 0;
    const allLivestock = classified.filter((item) => item.kind === 'fish' || item.kind === 'shrimp');
    const visible = allLivestock.slice(0, MAX_SWIMMERS); const wanted = new Set(visible.map((item) => item.key));
    water.querySelectorAll('[data-aq-mini-pond-swimmer]').forEach((node) => { if (!wanted.has(node.dataset.aqMiniPondSwimmer)) node.remove(); });
    visible.forEach((item) => upsertSwimmer(water, item));
    pond.dataset.aqMiniPondCount = String(allLivestock.length);
    status.textContent = allLivestock.length ? `${allLivestock.length} livestock ${allLivestock.length === 1 ? 'selection' : 'selections'} in your pond` : 'Your pond is quiet.';
    return allLivestock.length;
  }

  async function ensurePonds() {
    const targets = [...document.querySelectorAll('.cart-drawer__items, .cart-page__items')]; setCartBadge(0); if (!targets.length) return;
    const counts = await Promise.all(targets.map(async (target) => reconcile(ensurePond(target), target))); setCartBadge(Math.max(0, ...counts));
  }
  function schedule() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (scheduledFrame) window.cancelAnimationFrame(scheduledFrame);
    scheduledFrame = window.requestAnimationFrame(() => { scheduledFrame = window.requestAnimationFrame(() => { scheduledFrame = 0; ensurePonds(); }); });
  }
  function boot() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    setCartBadge(0); schedule();
    document.addEventListener('cart:update', schedule); document.addEventListener('discount:update', schedule); document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('click', (event) => { if (event.target?.closest?.(CART_TRIGGER_SELECTOR)) schedule(); });
    window.addEventListener('pageshow', schedule);
  }
  return { boot, classifyProduct, classifyLivestockKind, normalizeTags, productJsonUrl, hashSeed, setCartBadge };
});
